import React, { useState, useEffect, useCallback, useRef } from "react";
import { PhoneCall, Upload } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function ContactCaller() {
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/contacts-list`
      );
      const data = await res.json();

      if (data.success && Array.isArray(data.data)) {
        const formatted = data.data.map((c, i) => ({
          id: c._id || i,
          name: c.name || "Unknown",
          countryCode: c.country_code || "+91",
          phone: c.phone || "",
          createdAt: c.created_at || "",
        }));
        setContacts(formatted);
        toast.success(data.message || "Contacts fetched successfully");
      }
    } catch (err) {
      console.error("Fetch contacts error:", err);
      toast.error("Error fetching contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchContacts();
    }
  }, [fetchContacts]);

  // ============================
  // SELECT HANDLERS
  // ============================
  const handleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === contacts.length ? [] : contacts.map((c) => c.id)
    );
  }, [contacts]);

  // ============================
  // CALL HANDLERS
  // ============================
  const handleCallSingle = useCallback((contact, type = "traditional") => {
    const fullNumber = `${contact.countryCode || "+91"}${contact.phone}`;
    toast.success(
      `${type === "agent" ? "Agent" : "Traditional"} call to ${
        contact.name
      } (${fullNumber})`
    );
    console.log(
      `📞 ${type === "agent" ? "Agent" : "Traditional"} Call:`,
      contact
    );
  }, []);

  const handleCallSelected = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one contact to call!");
      return;
    }
    const selectedContacts = contacts.filter((c) => selectedIds.includes(c.id));
    const names = selectedContacts.map((c) => c.name).join(", ");
    toast.success(`Calling selected contacts: ${names}`);
    console.log("📞 Calling selected contacts:", selectedContacts);
  }, [contacts, selectedIds]);

  const handleImportExcel = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        toast.loading("Importing contacts...", { id: "import" });
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/contacts/import`,
          {
            method: "POST",
            body: formData,
          }
        );
        const data = await res.json();
        toast.dismiss("import");

        if (data.success) {
          toast.success(data.message || "Contacts imported successfully!");
          fetchContacts(); // refresh
        } else {
          toast.error(data.message || "Failed to import contacts.");
        }
      } catch (error) {
        toast.dismiss("import");
        console.error("Excel import error:", error);
        toast.error("Error uploading Excel file.");
      } finally {
        e.target.value = "";
      }
    },
    [fetchContacts]
  );

  const handleaddSingle = async (countrycode, phone) => {
    // console.log(phone, countrycode);
    const fullNumber = `${countrycode || "+91"}${phone}`;

    try {
      const payload = {
        caller: "+911203214860",
        destination: fullNumber,
      };
      const res = await fetch(
        `https://voipaiagent-api.greymatterz.com/api/v1/customoutbound`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (data) {
        toast.success(data.message || "Call successfully!");
      } else {
        // toast.error(data.message || "Failed to import contacts.");
      }
    } catch (error) {
      console.error("Call error:", error);
    } finally {
    }
  };

  return (
    <div className="container mt-5">
      {/* <Toaster richColors position="top-right" /> */}
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white">
          <h4 className="m-0 d-flex align-items-center">
            <PhoneCall size={22} className="me-2" /> Contact Caller
          </h4>
          <div>
            <label htmlFor="excelUpload" className="btn btn-light btn-sm me-2">
              <Upload size={16} className="me-1" /> Import Excel
            </label>
            <input
              id="excelUpload"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleImportExcel}
              style={{ display: "none" }}
            />
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5 text-muted">
              Loading contacts...
            </div>
          ) : (
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th scope="col">
                    <input
                      type="checkbox"
                      checked={
                        contacts.length > 0 &&
                        selectedIds.length === contacts.length
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th scope="col">Name</th>
                  <th scope="col">Country Code</th>
                  <th scope="col">Phone</th>
                  <th scope="col" className="text-end">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No contacts found.
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact) => (
                    <tr key={contact.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(contact.id)}
                          onChange={() => handleSelect(contact.id)}
                        />
                      </td>
                      <td>{contact.name}</td>
                      <td>{contact.countryCode}</td>
                      <td>{contact.phone}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => {
                            handleaddSingle(contact.countryCode, contact.phone);
                          }}
                        >
                          <PhoneCall size={16} className="me-1" />
                          Traditional Call
                        </button>

                        <button
                          className="btn btn-success btn-sm ms-2"
                          onClick={() => handleCallSingle(contact, "agent")}
                        >
                          <PhoneCall size={16} className="me-1" />
                          Agent Call
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-footer text-end">
          <button
            className="btn btn-success"
            onClick={handleCallSelected}
            disabled={selectedIds.length === 0}
          >
            <PhoneCall size={16} className="me-2" />
            Call Selected ({selectedIds.length})
          </button>
        </div>
      </div>
    </div>
  );
}
